import { useState } from 'react';
import {
  AritoFormV2,
  AritoIcon,
  BottomActionBarV2,
  BottomBarActionButtonV2,
  ConfirmationDialog,
  DialogV2,
  FormField
} from '@/components/custom/erp';
import { FormSchema, initialValues, type PaymentTermSubmitData } from '../schema';
import type { HanThanhToan } from '@/types/schemas';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

interface PaymentTermDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit' | 'view';
  initialData?: HanThanhToan | null;
  selectedObj: HanThanhToan | null;
  addPaymentTerm: (data: PaymentTermSubmitData) => Promise<unknown>;
  updatePaymentTerm: (data: PaymentTermSubmitData) => Promise<unknown>;
  onAddButtonClick?: () => void;
  onEditButtonClick?: () => void;
  onDeleteButtonClick?: () => void;
  onCopyButtonClick?: () => void;
}

// Component thông báo đơn giản chỉ có nút "Đồng ý"
const NotificationDialog = ({
  open,
  onClose,
  title = 'Thông báo',
  message,
  iconCode = 260
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  iconCode?: number;
}) => {
  return (
    <DialogV2
      open={open}
      onClose={onClose}
      title={title}
      maxWidth='sm'
      disableBackdropClose={false}
      disableEscapeKeyDown={false}
      showFullscreenToggle={false}
      titleIcon={<AritoIcon icon={iconCode} />}
      actions={<BottomBarActionButtonV2 icon={884} title='Đồng ý' onClick={onClose} />}
    >
      <p className='min-w-96 p-4 text-base font-medium'>{message}</p>
    </DialogV2>
  );
};

const PaymentTermForm = ({ mode }: { mode: 'add' | 'edit' | 'view' }) => {
  const isDisabled = mode === 'view';

  return (
    <div className='max-h-[calc(100vh-150px)] overflow-y-auto'>
      <div className='p-4 md:p-6'>
        <div className='flex flex-col gap-y-4 md:gap-y-6'>
          <div className='space-y-4 md:space-y-6'>
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Mã thanh toán</Label>
              <FormField type='text' name='ma_tt' disabled={mode === 'view' || mode === 'edit'} />
            </div>

            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Tên thanh toán</Label>
              <FormField type='text' name='ten_tt' disabled={isDisabled} />
            </div>

            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Tên khác</Label>
              <FormField type='text' name='ten_tt2' disabled={isDisabled} />
            </div>

            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>
                Hạn thanh toán <span className='text-red-500'>*</span>
              </Label>
              <FormField type='number' name='han_tt' disabled={isDisabled} />
            </div>

            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label htmlFor='status' className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>
                Trạng thái
              </Label>
              <FormField
                id='status'
                type='select'
                name='status'
                disabled={isDisabled}
                options={[
                  { value: '0', label: '0. Không sử dụng' },
                  { value: '1', label: '1. Còn sử dụng' }
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentTermDialog = ({
  open,
  onClose,
  mode,
  initialData,
  selectedObj,
  addPaymentTerm,
  updatePaymentTerm,
  onAddButtonClick,
  onEditButtonClick,
  onDeleteButtonClick,
  onCopyButtonClick
}: PaymentTermDialogProps) => {
  const { toast } = useToast();
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showNegativeValueDialog, setShowNegativeValueDialog] = useState<boolean>(false);

  const handleSubmit = async (data: PaymentTermSubmitData) => {
    // Kiểm tra trường tên thanh toán không được để trống
    if (!data.ten_tt || data.ten_tt.trim() === '') {
      setErrorMessage('Tên thanh toán không được để trống');
      setShowErrorDialog(true);
      return; // Không đóng form, chỉ hiển thị dialog lỗi
    }

    // Kiểm tra trường hạn thanh toán trước khi submit
    if (data.han_tt === undefined || data.han_tt === null || data.han_tt === '') {
      setErrorMessage('Hạn thanh toán không được để trống');
      setShowErrorDialog(true);
      return; // Không đóng form, chỉ hiển thị dialog lỗi
    }

    // Kiểm tra hạn thanh toán không được âm
    const hanThanhToan = typeof data.han_tt === 'string' ? parseFloat(data.han_tt) : data.han_tt;
    if (hanThanhToan < 0) {
      setShowNegativeValueDialog(true);
      return; // Không đóng form, chỉ hiển thị dialog thông báo
    }

    try {
      if (mode === 'add') {
        await addPaymentTerm(data);
        toast({
          description: 'Thêm hạn thanh toán thành công',
          variant: 'default'
        });
        onClose();
      } else if (mode === 'edit') {
        await updatePaymentTerm({ ...data, uuid: selectedObj?.uuid });
        toast({
          description: 'Cập nhật hạn thanh toán thành công',
          variant: 'default'
        });
        onClose();
      }
    } catch (err: unknown) {
      toast({
        description: err instanceof Error ? err.message : 'Có lỗi xảy ra',
        variant: 'destructive'
      });
    }
  };

  const title = mode === 'add' ? 'Thêm hạn thanh toán' : mode === 'edit' ? 'Sửa hạn thanh toán' : 'Xem hạn thanh toán';

  return (
    <>
      <DialogV2
        open={open}
        onClose={onClose}
        title={title}
        maxWidth='md'
        disableBackdropClose={false}
        disableEscapeKeyDown={false}
      >
        <AritoFormV2
          mode={mode}
          hasAritoActionBar={false}
          onSubmit={handleSubmit}
          initialData={(initialData as Partial<PaymentTermSubmitData> | null | undefined) || initialValues}
          schema={FormSchema}
          className='w-full md:min-w-[500px] lg:min-w-[600px]'
          headerFields={<PaymentTermForm mode={mode} />}
          bottomBar={
            <>
              <BottomActionBarV2
                mode={mode}
                onAdd={onAddButtonClick}
                onEdit={onEditButtonClick}
                onDelete={onDeleteButtonClick}
                onCopy={onCopyButtonClick}
                onClose={onClose}
              />
            </>
          }
        />
      </DialogV2>

      {/* Dialog thông báo lỗi */}
      {showErrorDialog && (
        <ConfirmationDialog
          open={showErrorDialog}
          onClose={() => setShowErrorDialog(false)}
          onConfirm={() => setShowErrorDialog(false)}
          title='Thông báo'
          message={errorMessage}
          iconCode={260}
        />
      )}

      {/* Dialog thông báo số âm */}
      {showNegativeValueDialog && (
        <NotificationDialog
          open={showNegativeValueDialog}
          onClose={() => setShowNegativeValueDialog(false)}
          title='Thông báo'
          message='Hạn thanh toán không được là số âm'
          iconCode={260}
        />
      )}
    </>
  );
};

export default PaymentTermDialog;
